/* eslint-disable */
import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";

import VisitMachineService, {
  getInitialValuesPerMachineErrorInfo,
} from "services/visitMachineService";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppNumberInput from "components/common/AppNumberInput";
import InitialValuesMachineSummary from "forms/localMachine/InitialValuesMachineSummary";

const DEFAULT_NOTE = "Ingreso de valores iniciales";

const normalizeNumberString = (value) =>
  String(value ?? "")
    .trim()
    .replace(",", ".");

const parseFiniteNumberOrNull = (value) => {
  const normalized = normalizeNumberString(value);
  if (normalized === "") return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCounterValueForForm = (value) => {
  if (value == null) return "";
  const normalized = normalizeNumberString(value);
  if (normalized === "") return "";
  const parsed = Number(normalized);
  if (Number.isFinite(parsed) && Number.isInteger(parsed)) {
    return String(parsed);
  }
  return String(value);
};

const buildValidationErrors = (form) => {
  const nextErrors = {};
  const integerCounterError = "Ingresá un número entero mayor o igual a 0.";
  const validateCounterInteger = (field) => {
    if (normalizeNumberString(form[field]) === "") return;
    const parsed = parseFiniteNumberOrNull(form[field]);
    if (parsed == null || !Number.isInteger(parsed) || parsed < 0) {
      nextErrors[field] = integerCounterError;
    }
  };

  if (normalizeNumberString(form.PostResetStartIn) === "") {
    nextErrors.PostResetStartIn = "La entrada es obligatoria.";
  } else if (parseFiniteNumberOrNull(form.PostResetStartIn) == null) {
    nextErrors.PostResetStartIn = "La entrada debe ser numérica.";
  }

  if (normalizeNumberString(form.PostResetStartOut) === "") {
    nextErrors.PostResetStartOut = "La salida es obligatoria.";
  } else if (parseFiniteNumberOrNull(form.PostResetStartOut) == null) {
    nextErrors.PostResetStartOut = "La salida debe ser numérica.";
  }

  validateCounterInteger("PostResetStartIn");
  validateCounterInteger("PostResetStartOut");

  return nextErrors;
};

function InitialValuesPerMachineForm({
  localMachine,
  closeForm,
  onSuccess,
  onSavingChange,
  onStateRefreshRequested,
}) {
  const [form, setForm] = React.useState({
    PostResetStartIn: "",
    PostResetStartOut: "",
  });
  const [errors, setErrors] = React.useState({});
  const [backendError, setBackendError] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoadingState, setIsLoadingState] = React.useState(true);
  const [initialValuesState, setInitialValuesState] = React.useState(null);

  const resolvedLocalMachineId = localMachine?.Id ?? localMachine?.LocalMachineId ?? null;
  const fallbackStartDateTime = localMachine?.StartDateTime ?? null;
  const formResetKey = React.useMemo(
    () => String(localMachine?.Id ?? localMachine?.LocalMachineId ?? "new"),
    [localMachine?.Id, localMachine?.LocalMachineId]
  );

  React.useEffect(() => {
    let alive = true;

    async function loadInitialValuesState() {
      if (!resolvedLocalMachineId) {
        setInitialValuesState({
          found: false,
          mode: "blocked",
          visitMachineId: null,
          visitId: null,
          installationId: null,
          historicalLocalMachineId: null,
          postResetStartIn: null,
          postResetStartOut: null,
          visitDateTime: null,
          hasSubsequentCollections: false,
          canEdit: false,
          blockReason: "No se encontró un LocalMachineId válido para guardar.",
        });
        setBackendError("No se encontró un LocalMachineId válido para guardar.");
        setIsLoadingState(false);
        return;
      }

      setIsLoadingState(true);
      setBackendError("");
      setErrors({});

      try {
        const state = await VisitMachineService.getInitialValuesForLocalMachine(resolvedLocalMachineId);
        if (!alive) return;

        setInitialValuesState(state || null);
        setForm({
          PostResetStartIn:
            state?.mode === "edit" && state?.postResetStartIn != null
              ? normalizeCounterValueForForm(state.postResetStartIn)
              : "",
          PostResetStartOut:
            state?.mode === "edit" && state?.postResetStartOut != null
              ? normalizeCounterValueForForm(state.postResetStartOut)
              : "",
        });
      } catch (error) {
        if (!alive) return;
        const errorInfo = getInitialValuesPerMachineErrorInfo(error);
        setInitialValuesState({
          found: false,
          mode: "blocked",
          visitMachineId: null,
          visitId: null,
          installationId: null,
          historicalLocalMachineId: null,
          postResetStartIn: null,
          postResetStartOut: null,
          visitDateTime: null,
          hasSubsequentCollections: false,
          canEdit: false,
          blockReason: errorInfo.message,
        });
        setBackendError(errorInfo.message);
      } finally {
        if (alive) setIsLoadingState(false);
      }
    }

    setForm({
      PostResetStartIn: "",
      PostResetStartOut: "",
    });
    setErrors({});
    setBackendError("");
    setIsSaving(false);
    setInitialValuesState(null);
    loadInitialValuesState();

    return () => {
      alive = false;
    };
  }, [formResetKey, resolvedLocalMachineId]);

  React.useEffect(() => {
    onSavingChange?.(isSaving || isLoadingState);
  }, [isSaving, isLoadingState, onSavingChange]);

  const mode = initialValuesState?.mode ?? "create";
  const isBlocked = mode === "blocked";
  const isEditMode = mode === "edit";
  const saveDisabled = isSaving || isLoadingState || isBlocked;
  const startDateTime = fallbackStartDateTime ?? initialValuesState?.visitDateTime;

  const handleNumberChange = (field, nextValue) => {
    if (saveDisabled) return;
    setForm((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
    setBackendError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saveDisabled) return;

    const nextErrors = buildValidationErrors(form);
    setErrors(nextErrors);
    setBackendError("");

    if (Object.keys(nextErrors).length > 0) return;
    if (!resolvedLocalMachineId) {
      setBackendError("No se encontró un LocalMachineId válido para guardar.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        PostResetStartIn: parseFiniteNumberOrNull(form.PostResetStartIn),
        PostResetStartOut: parseFiniteNumberOrNull(form.PostResetStartOut),
        Note: DEFAULT_NOTE,
      };

      const result = isEditMode
        ? await VisitMachineService.updateInitialValuesForLocalMachine(
            resolvedLocalMachineId,
            payload
          )
        : await VisitMachineService.createInitialValuesForLocalMachine(
            resolvedLocalMachineId,
            payload
          );

      await onSuccess?.(result);
    } catch (error) {
      const errorInfo = getInitialValuesPerMachineErrorInfo(error);
      setBackendError(errorInfo.message);

      if (errorInfo.shouldRefresh) {
        try {
          await onStateRefreshRequested?.();
          const refreshedState = await VisitMachineService.getInitialValuesForLocalMachine(
            resolvedLocalMachineId
          );
          setInitialValuesState(refreshedState || null);
          setForm({
            PostResetStartIn:
              refreshedState?.mode === "edit" && refreshedState?.postResetStartIn != null
                ? normalizeCounterValueForForm(refreshedState.postResetStartIn)
                : "",
            PostResetStartOut:
              refreshedState?.mode === "edit" && refreshedState?.postResetStartOut != null
                ? normalizeCounterValueForForm(refreshedState.postResetStartOut)
                : "",
          });
        } catch (refreshError) {
          console.error("Error refrescando estado tras fallo de initial values:", refreshError);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container component={Paper} style={{ padding: "20px", marginTop: "10px", width: "100%" }}>
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InitialValuesMachineSummary
              localMachine={localMachine}
              startDateTime={startDateTime}
            />
          </Grid>

          {isLoadingState && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">
                  Cargando estado de valores iniciales...
                </Typography>
              </Box>
            </Grid>
          )}

          {isBlocked && !isLoadingState && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {initialValuesState?.blockReason ||
                  "Los valores iniciales ya no se pueden editar para esta instalación."}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <AppNumberInput
              fullWidth
              required
              disabled={saveDisabled}
              label="Entrada"
              value={form.PostResetStartIn}
              onValueChange={(value) => handleNumberChange("PostResetStartIn", value)}
              decimalScale={0}
              allowNegative={false}
              min={0}
              error={Boolean(errors.PostResetStartIn)}
              helperText={errors.PostResetStartIn}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <AppNumberInput
              fullWidth
              required
              disabled={saveDisabled}
              label="Salida"
              value={form.PostResetStartOut}
              onValueChange={(value) => handleNumberChange("PostResetStartOut", value)}
              decimalScale={0}
              allowNegative={false}
              min={0}
              error={Boolean(errors.PostResetStartOut)}
              helperText={errors.PostResetStartOut}
            />
          </Grid>

          {backendError && (
            <Grid item xs={12}>
              <Typography variant="caption" color="error">
                {backendError}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CancelButton onClick={closeForm} disabled={isSaving} fullWidth>
                  Cancelar
                </CancelButton>
              </Grid>

              <Grid item xs={12} md={6}>
                <AcceptButton type="submit" disabled={saveDisabled} fullWidth>
                  {isSaving
                    ? "Guardando..."
                    : "Guardar valores iniciales"}
                </AcceptButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}

InitialValuesPerMachineForm.propTypes = {
  localMachine: PropTypes.object,
  closeForm: PropTypes.func,
  onSuccess: PropTypes.func,
  onSavingChange: PropTypes.func,
  onStateRefreshRequested: PropTypes.func,
};

export default InitialValuesPerMachineForm;
